import { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
    TextField, CircularProgress, Alert,
} from '@mui/material';
import { bulkPayout } from '../../api/commissionProgressApi';

const PP = "'Poppins', sans-serif";

// Bulk commission payout (user 2026-08-20).
//
// The operator selects rows and EVERY ROW FILLS ITSELF from its own customer's payment:
// 60% collected releases 60% of the commission, 100% releases the balance. There is no
// tranche to pick — the customer's payment already decides it. A batch can therefore
// land on a different number, and a different share, for every row.
//
// Two-step by design: the dialog always previews (dryRun) before anything is written, and
// blocked rows are LISTED with a reason rather than silently dropped. A half-applied batch
// that reads as a success is how someone re-runs it and double-pays the rows that worked.
export default function BulkPayoutDialog({ open, onClose, selectedRows, onDone, isSuperAdmin = false }) {
    // Kept only so the request shape stays stable; the SERVER decides the tranche from
    // what the customer has paid, so there is nothing for the operator to choose.
    const milestonePct = 100;
    const [amount, setAmount] = useState('');          // blank = autofill per row
    // Per-row edits, keyed by commProgressId. Mirrors the single-row modal, where an
    // accountant sees the remaining amount prefilled and can type over it.
    const [rowAmounts, setRowAmounts] = useState({});
    const [rowDates, setRowDates] = useState({});
    // The % a row is being typed as, kept raw while editing so "4" on the way to "40"
    // isn't reformatted under the cursor. Cleared when the amount is typed instead.
    const [rowPcts, setRowPcts] = useState({});
    const [paymentOn, setPaymentOn] = useState(() => new Date().toISOString().slice(0, 10));
    const [override, setOverride] = useState(false);
    const [overrideReason, setOverrideReason] = useState('');
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const ids = (selectedRows || [])
        .map(r => r.original?.commProgressId ?? r.commProgressId)
        .filter(Boolean);

    const payload = useCallback((dryRun) => ({
        commProgressIds: ids,
        milestonePct,
        amount: amount === '' ? null : Number(amount),
        rowAmounts: Object.fromEntries(Object.entries(rowAmounts)
            .filter(([, v]) => v !== '' && v != null && !Number.isNaN(Number(v)))
            .map(([k, v]) => [Number(k), Number(v)])),
        rowDates: Object.fromEntries(Object.entries(rowDates)
            .filter(([, v]) => !!v)
            .map(([k, v]) => [Number(k), v])),
        paymentOn,
        dryRun,
        bypassGate: override,
        overrideReason: override ? overrideReason : null,
    }), [ids, milestonePct, amount, paymentOn, override, overrideReason, rowAmounts, rowDates]);

    // Re-preview whenever anything that changes the numbers changes.
    useEffect(() => {
        if (!open || ids.length === 0) return;
        // A fresh open starts from the autofills, never from a previous batch's edits.
        let cancelled = false;
        setLoading(true); setError('');
        bulkPayout(payload(true))
            .then(res => { if (!cancelled) setPreview(res.data); })
            .catch(e => { if (!cancelled) setError(e?.response?.data?.message || 'Could not build the preview.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // NOTE: rowAmounts / rowDates are deliberately NOT dependencies. They change on
        // every keystroke, and re-previewing per character made the field re-render mid-typing
        // and lose the caret. Per-row edits are evaluated locally against the cap the preview
        // already returned; the server still validates every row on commit, which is what
        // actually matters. (user 2026-08-21)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, milestonePct, amount, paymentOn, override, overrideReason]);

    const commit = async () => {
        setSaving(true); setError('');
        try {
            const res = await bulkPayout(payload(false));
            onDone && onDone(res.data);
            onClose && onClose();
        } catch (e) {
            setError(e?.response?.data?.message || 'The batch could not be written.');
        } finally {
            setSaving(false);
        }
    };

    const rm = (n) => `RM ${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    // Per-row edits are evaluated HERE rather than by re-previewing, so typing stays smooth.
    // The server re-checks every one of these on commit — this is for display only.
    const effFill = (r) => {
        const v = rowAmounts[r.commProgressId];
        return (v === '' || v == null || Number.isNaN(Number(v))) ? r.fill : Number(v);
    };
    const effPct = (r) => (r.commPay ? Math.round((effFill(r) / r.commPay) * 1000) / 10 : 0);
    // Same sign-safe rule the server uses: a cap of -90.88 must not accept -500.
    const overCap = (r) => {
        const f = effFill(r), c = r.cap;
        if (c === 0) return f !== 0;
        return c > 0 ? (f < 0 || f > c) : (f > 0 || f < c);
    };

    const payable = preview?.rows?.filter(r => !r.blocked) || [];
    const blocked = preview?.rows?.filter(r => r.blocked) || [];
    const offending = payable.filter(overCap);
    const liveTotal = payable.reduce((s, r) => s + effFill(r), 0);
    // Rows where the customer's payment forced a smaller tranche than the one requested.
    const partialRows = payable.filter(r => r.appliedPct === 60);

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.05rem' }}>
                Pay Selected ({ids.length})
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', mb: 2 }}>
                    {/* Both of these are "apply to every row" controls, so they read the same
                        way. Leaving Amount empty keeps each row's own autofill. (user 2026-08-21) */}
                    <TextField size="small" label="Fill all amounts" value={amount} type="number"
                        onChange={e => setAmount(e.target.value)} sx={{ width: 190 }} />
                    <TextField size="small" label="Fill all dates" type="date" value={paymentOn}
                        onChange={e => setPaymentOn(e.target.value)} InputLabelProps={{ shrink: true }} />
                    {isSuperAdmin && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button size="small" variant={override ? 'contained' : 'outlined'} color="warning"
                                onClick={() => setOverride(v => !v)}
                                sx={{ fontFamily: PP, textTransform: 'none' }}>
                                {override ? 'Override on' : 'Override gate'}
                            </Button>
                            {override && (
                                <TextField size="small" label="Reason (required)" value={overrideReason}
                                    onChange={e => setOverrideReason(e.target.value)} sx={{ width: 240 }} />
                            )}
                        </Box>
                    )}
                </Box>

                <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#64748B', mb: 1.5 }}>
                    Every row is filled automatically from what its own customer has paid &mdash; 60% collected
                    releases 60% of the commission, 100% releases the balance. Type over any figure to pay less.
                </Typography>

                {/* The gate caps a row to what its customer has actually paid. Without saying
                    so, picking 100% and getting 60% amounts looks like the gate is broken
                    rather than working. Make the cap visible. (user 2026-08-20) */}
                {!loading && !error && partialRows.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2, fontFamily: PP, fontSize: '0.83rem' }}>
                        <strong>{partialRows.length} row{partialRows.length === 1 ? '' : 's'} on the 60% tranche.</strong>{' '}
                        {partialRows.length === 1 ? 'That customer has' : 'Those customers have'} paid 60% but not in full,
                        so only 60% of the commission is released. The balance becomes available once they reach 100%.
                    </Alert>
                )}

                {error && <Alert severity="error" sx={{ mb: 2, fontFamily: PP }}>{error}</Alert>}
                {loading && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>}

                {!loading && preview && (
                    <>
                        <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.82rem' }}>
                                <strong>{payable.length}</strong> payable
                            </Typography>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: blocked.length ? '#B3261E' : undefined }}>
                                <strong>{blocked.length}</strong> blocked
                            </Typography>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.82rem' }}>
                                Total: <strong>{rm(liveTotal)}</strong>
                            </Typography>
                        </Box>

                        <Box sx={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: 2 }}>
                            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem', fontFamily: PP, minWidth: 820 }}>
                                <thead>
                                    <tr style={{ background: '#F8FAFC' }}>
                                        {['Sale', 'Customer', 'Type', 'Paid / claimed to', 'Commission', 'Already taken', 'Cust paid', 'Payment', '% of comm', 'Date', 'Note']
                                            .map(h => <th key={h} style={{ textAlign: 'left', padding: '7px 10px', borderBottom: '1px solid #E2E8F0', fontSize: '0.68rem', textTransform: 'uppercase', color: '#64748B' }}>{h}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.rows.map(r => (
                                        <tr key={r.commProgressId} style={{ background: r.blocked ? '#FDF2F2' : undefined }}>
                                            <td style={{ padding: '6px 10px' }}>{r.salesId}</td>
                                            <td style={{ padding: '6px 10px' }}>{r.customerName}</td>
                                            <td style={{ padding: '6px 10px' }}>{r.commPayType}</td>
                                            <td style={{ padding: '6px 10px' }}>{r.paymentToName}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right' }}>{rm(r.commPay)}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right' }}>{rm(r.alreadyPaid)}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right' }}>{r.customerPaidPct}%</td>
                                            <td style={{ padding: '4px 10px', textAlign: 'right' }}>
                                                {r.blocked ? (
                                                    <span style={{ color: '#94A3B8' }}>—</span>
                                                ) : (
                                                    <input
                                                        type="number"
                                                        value={rowAmounts[r.commProgressId] ?? r.fill}
                                                        onChange={e => {
                                                            setRowAmounts(prev => ({ ...prev, [r.commProgressId]: e.target.value }));
                                                            // Typing an amount hands the % back to the derived figure.
                                                            setRowPcts(prev => { const n = { ...prev }; delete n[r.commProgressId]; return n; });
                                                        }}
                                                        style={{ width: 104, textAlign: 'right', fontWeight: 700, fontFamily: PP,
                                                                 fontSize: '0.8rem', padding: '4px 7px', borderRadius: 6,
                                                                 border: '1px solid #CBD5E1',
                                                                 borderColor: overCap(r) ? '#B3261E' : '#CBD5E1',
                                                                 color: overCap(r) ? '#B3261E' : (effFill(r) < 0 ? '#B3261E' : '#1B6B3A') }}
                                                        title={overCap(r) ? `Max ${rm(r.cap)} on this row` : undefined} />
                                                )}
                                            </td>
                                            {/* What share of the WHOLE commission this payment is, so the
                                                operator can see "this is the 40% balance" at a glance
                                                rather than inferring it. (user 2026-08-20) */}
                                            <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                                                {r.blocked ? (
                                                    <span style={{ color: '#94A3B8' }}>—</span>
                                                ) : (
                                                    // Editable: typing a % sets the payment to that share of the
                                                    // commission, and typing a payment recomputes the %. (user 2026-09-17)
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            aria-label={`% of commission for sale ${r.salesId} ${r.commPayType}`}
                                                            value={rowPcts[r.commProgressId] ?? effPct(r)}
                                                            disabled={!r.commPay}
                                                            onChange={e => {
                                                                const raw = e.target.value;
                                                                setRowPcts(prev => ({ ...prev, [r.commProgressId]: raw }));
                                                                const p = Number(raw);
                                                                if (raw !== '' && !Number.isNaN(p) && r.commPay) {
                                                                    const amt = Math.round(r.commPay * p) / 100;
                                                                    setRowAmounts(prev => ({ ...prev, [r.commProgressId]: String(amt) }));
                                                                }
                                                            }}
                                                            style={{
                                                                width: 62, textAlign: 'right', fontFamily: PP, fontSize: '0.75rem', fontWeight: 700,
                                                                padding: '3px 5px', borderRadius: 4,
                                                                background: r.appliedPct === 100 ? '#F0FDF4' : '#EFF6FF',
                                                                color: overCap(r) ? '#B3261E' : (r.appliedPct === 100 ? '#15803D' : '#1D4ED8'),
                                                                border: `1px solid ${overCap(r) ? '#B3261E' : (r.appliedPct === 100 ? '#BBF7D0' : '#BFDBFE')}`,
                                                            }} />
                                                        <span style={{ fontFamily: PP, fontSize: '0.72rem', fontWeight: 700, color: '#64748B' }}>%</span>
                                                    </span>
                                                )}
                                            </td>
                                            {/* Per-row payout date, prefilled from the batch date. Mirrors the
                                                single-row modal, where every pay slot carries its own date. */}
                                            <td style={{ padding: '4px 10px' }}>
                                                {r.blocked ? (
                                                    <span style={{ color: '#94A3B8' }}>—</span>
                                                ) : (
                                                    <input
                                                        type="date"
                                                        value={rowDates[r.commProgressId] ?? paymentOn}
                                                        onChange={e => setRowDates(prev => ({ ...prev, [r.commProgressId]: e.target.value }))}
                                                        style={{ fontFamily: PP, fontSize: '0.78rem', padding: '4px 7px',
                                                                 borderRadius: 6, border: '1px solid #CBD5E1', color: '#374151' }} />
                                                )}
                                            </td>
                                            <td style={{ padding: '6px 10px', color: r.blocked ? '#B3261E' : '#64748B', fontSize: '0.74rem' }}>
                                                {r.blocked ? r.reason : (
                                                    <>
                                                        {r.kind === 'claim' && (
                                                            <span style={{
                                                                display: 'inline-block', marginRight: 6, padding: '1px 6px', borderRadius: 3,
                                                                background: '#EEF2FF', color: '#3730A3', border: '1px solid #C7D2FE',
                                                                fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.04em',
                                                            }}>CLAIM</span>
                                                        )}
                                                        {`${r.appliedPct}% → ${r.field} ${r.kind === 'claim' ? 'Claim' : 'Payment'}`}
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={saving} sx={{ fontFamily: PP, textTransform: 'none', color: '#64748B' }}>Cancel</Button>
                <Button onClick={commit} variant="contained"
                    disabled={saving || loading || payable.length === 0 || offending.length > 0}
                    sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600 }}>
                    {saving ? 'Writing…'
                        : offending.length > 0 ? `${offending.length} row${offending.length === 1 ? '' : 's'} over the allowance`
                        : `Pay ${payable.length} row${payable.length === 1 ? '' : 's'} — ${rm(liveTotal)}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
